// Shape of `Job.output` for `PROCESS_AMAZON_WADM` jobs (ENG-15148).
//
// Single source of truth for the Lambda that produces the summary and the Oak
// frontend that renders it. Both import from here so the shape can't drift.

export type CupaBreakdownEntry = {
  lepcName: string | null;
  county: string | null;
  reportCount: number;
};

export type InventoryChangeDetail = {
  facilityId: string;
  facilityName: string | null;
  chemicalId: string;
  chemicalName: string | null;
  kind: "created" | "updated" | "unchanged";
  priorMaxAmount: number | null;
  newMaxAmount: number;
  priorMaxAmountCode: number | null;
  newMaxAmountCode: number | null;
  priorAverageAmount: number | null;
  /**
   * WADM component plus the sum of non-WADM storage-row averages — no longer
   * mirrors `newMaxAmount` when a customer row's average differs from its max.
   */
  newAverageAmount: number;
  priorAverageAmountCode: number | null;
  newAverageAmountCode: number | null;
  /** Reporting unit the amounts above are expressed in (e.g. "GALLONS", "CUBIC_FEET", "POUNDS"). */
  unit: string;
  /** Σ of all period amounts for the calendar year, in `unit`. */
  annualWasteAmount: number;
};

/** Why a facility's rows were skipped this run (instead of aborting the whole run). */
export type BlockedFacilityReason =
  | "unmatchedProfileCode"
  | "ambiguousChemical"
  | "missingChemical"
  | "colocationParentNotInCalifornia"
  | "missingDensity"
  | "missingCersId";

export const BLOCKED_FACILITY_REASONS: readonly BlockedFacilityReason[] = [
  "unmatchedProfileCode",
  "ambiguousChemical",
  "missingChemical",
  "colocationParentNotInCalifornia",
  "missingDensity",
  "missingCersId",
];

export type BlockedFacility = {
  /** Amazon siteCode (normalized) whose rows were skipped. */
  siteCode: string;
  /** The matched facility, when the siteCode resolved to one; null otherwise. */
  facilityId: string | null;
  /** Display name of `facilityId`. Optional: older records predate it. */
  facilityName?: string | null;
  /** Deduped, sorted reasons the facility was skipped. */
  reasons: BlockedFacilityReason[];
};

/**
 * A facility the matcher found for a site code but refused because of its
 * status (CLOSED / INACTIVE). Surfaced so the operator can tell "no facility
 * exists" apart from "the facility exists but is retired".
 */
export type ExcludedFacilityMatch = {
  facilityId: string;
  facilityName: string;
  /** A `FacilityStatus` value (CLOSED or INACTIVE); a string here so the shared package needs no Prisma import. */
  status: string;
};

/** Which step of the matcher cascade produced an ambiguous candidate. */
export type FacilityMatchSource = "epaId" | "name";

export const FACILITY_MATCH_SOURCES: readonly FacilityMatchSource[] = ["epaId", "name"];

/**
 * One of several eligible facilities that matched a site code. Surfaced so the
 * operator can tell "nothing matched" apart from "too many matched" and see
 * which rows to reconcile.
 */
export type AmbiguousFacilityMatch = {
  facilityId: string;
  facilityName: string;
  /** 2-letter state code of the candidate. */
  state: string;
  matchedBy: FacilityMatchSource;
};

export type UnmatchedSite = {
  /** Amazon siteCode (normalized) that resolved to no eligible facility. */
  siteCode: string;
  /** EPA id carried on the site's WADM rows, when present. */
  regulatoryId: string | null;
  /** The only facility that matched, when it was rejected for its status. */
  excludedMatch: ExcludedFacilityMatch | null;
  /**
   * Every eligible facility that matched when more than one did, so the site
   * could not be resolved. Absent when the site simply had no match.
   */
  ambiguousMatches?: AmbiguousFacilityMatch[];
};

export type MissingDensityCause =
  /** The chemical reports in gallons / cubic feet but has no density to convert pounds. */
  | "chemicalHasNoDensity"
  /** A customer storage row is in a different unit than the chemical, and converting it needs a density the chemical lacks. */
  | "storageRowUnitConversion";

export const MISSING_DENSITY_CAUSES: readonly MissingDensityCause[] = [
  "chemicalHasNoDensity",
  "storageRowUnitConversion",
];

export type MissingDensityChemical = {
  facilityId: string;
  facilityName: string | null;
  chemicalId: string;
  chemicalName: string | null;
  cause: MissingDensityCause;
};

export type FacilityRef = {
  facilityId: string;
  facilityName: string | null;
};

export type RunSummary = {
  wadmCycleId: string;
  /**
   * sha256 over the sorted set of `AmazonWadmItem.rowHash`es this run ingested.
   * Identifies the cycle by CONTENT, so a rerun of the same CSV uploaded under
   * a different s3Key (the key embeds the upload date) still merges into the
   * same cumulative summary. Optional: `Job.output` records written before
   * this field existed won't carry it, so consumers should treat absence as
   * "match by wadmCycleId only".
   */
  contentHash?: string;
  /** Total distinct siteCodes the run looked at (matched + out-of-state + unmatched). */
  facilitiesAttempted: number;
  /** Distinct siteCodes that resolved to a CA facility. The denominator for inventory/report work. */
  facilitiesMatched: number;
  itemsUpdated: number;
  /**
   * Facilities with an open UPDATE for this reporting year after the run —
   * sums drafts opened this run and drafts that were already open from a
   * prior cycle (those facilities appear in `facilityIdsWithExistingOpenUpdate`).
   */
  reportsCreated: number;
  /**
   * Facility ids skipped this run because an open UPDATE already existed for
   * the facility+reportingYear. Includes same-cycle retries and cross-cycle
   * pile-up candidates. Sorted.
   */
  facilityIdsWithExistingOpenUpdate: string[];
  /**
   * `facilityIdsWithExistingOpenUpdate` with display names, for the UI.
   * Optional: older records predate it; consumers fall back to the id list.
   */
  facilitiesWithExistingOpenUpdate?: FacilityRef[];
  /**
   * Distinct facility ids whose UPDATE draft was opened by ANY run of this
   * cycle (accumulated across reruns). `reportsCreated` and `cupaBreakdown`
   * are derived from this set so a rerun's summary reflects the whole cycle,
   * not just the facilities the latest run happened to touch. Sorted.
   * Optional: `Job.output` records written before this field existed won't
   * carry it, so consumers should treat absence as `[]`.
   */
  openedReportFacilityIds?: string[];
  unmatchedProfileCodes: string[];
  /**
   * Recognized-but-intentionally-unreported profile codes dropped this run (see
   * `WADM_EXCLUDED_PROFILES`). Unlike `unmatchedProfileCodes`, these do NOT
   * block their facility. Optional: `Job.output` records written before this
   * field existed won't carry it, so consumers should treat absence as `[]`.
   */
  excludedProfileCodes?: string[];
  missingChemicals: string[];
  unmatchedSiteCodes: string[];
  /**
   * `unmatchedSiteCodes` with the EPA id each site carried and, when the only
   * hit was a CLOSED / INACTIVE facility, which one. Optional: older records
   * predate it; consumers fall back to the plain code list.
   */
  unmatchedSites?: UnmatchedSite[];
  /**
   * Every (facility, chemical) held back this run because a unit conversion
   * needed a density the chemical doesn't have. The blocked-facility list only
   * names the facility; this names the chemical to fix. Optional: older records
   * predate it.
   */
  missingDensityChemicals?: MissingDensityChemical[];
  /**
   * Facilities whose rows were skipped this run because at least one of their
   * rows could not be mapped (unmatched profile code, or an ambiguous/missing
   * target chemical). Clean facilities are still written; the run then ends in
   * FAILED so the gap is surfaced, with this list naming what to fix. Sorted by
   * siteCode. Optional: `Job.output` records written before this field existed
   * won't carry it, so consumers should treat absence as `[]`.
   */
  blockedFacilities?: BlockedFacility[];
  cupaBreakdown: CupaBreakdownEntry[];
  /** Per-row detail for every FacilityChemical created/updated this run. `unchanged` rows are not included. */
  inventoryChanges: InventoryChangeDetail[];
};

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === "string");
}

function isNullableString(v: unknown): v is string | null {
  return v === null || typeof v === "string";
}

function isNullableNumber(v: unknown): v is number | null {
  return v === null || typeof v === "number";
}

function isCupaBreakdownEntry(v: unknown): v is CupaBreakdownEntry {
  if (!isPlainObject(v)) return false;
  if (!isNullableString(v.lepcName)) return false;
  if (!isNullableString(v.county)) return false;
  if (typeof v.reportCount !== "number") return false;
  return true;
}

function isInventoryChangeKind(v: unknown): v is InventoryChangeDetail["kind"] {
  return v === "created" || v === "updated" || v === "unchanged";
}

function isBlockedFacilityReason(v: unknown): v is BlockedFacilityReason {
  return BLOCKED_FACILITY_REASONS.some((reason) => reason === v);
}

function isBlockedFacility(v: unknown): v is BlockedFacility {
  if (!isPlainObject(v)) return false;
  if (typeof v.siteCode !== "string") return false;
  if (!isNullableString(v.facilityId)) return false;
  if (v.facilityName !== undefined && !isNullableString(v.facilityName)) return false;
  if (!Array.isArray(v.reasons)) return false;
  if (!v.reasons.every(isBlockedFacilityReason)) return false;
  return true;
}

function isExcludedFacilityMatch(v: unknown): v is ExcludedFacilityMatch {
  if (!isPlainObject(v)) return false;
  if (typeof v.facilityId !== "string") return false;
  if (typeof v.facilityName !== "string") return false;
  if (typeof v.status !== "string") return false;
  return true;
}

function isFacilityMatchSource(v: unknown): v is FacilityMatchSource {
  return FACILITY_MATCH_SOURCES.some((source) => source === v);
}

function isAmbiguousFacilityMatch(v: unknown): v is AmbiguousFacilityMatch {
  if (!isPlainObject(v)) return false;
  if (typeof v.facilityId !== "string") return false;
  if (typeof v.facilityName !== "string") return false;
  if (typeof v.state !== "string") return false;
  if (!isFacilityMatchSource(v.matchedBy)) return false;
  return true;
}

function isUnmatchedSite(v: unknown): v is UnmatchedSite {
  if (!isPlainObject(v)) return false;
  if (typeof v.siteCode !== "string") return false;
  if (!isNullableString(v.regulatoryId)) return false;
  if (v.excludedMatch !== null && !isExcludedFacilityMatch(v.excludedMatch)) return false;
  if (!isOptionalArrayOf(v.ambiguousMatches, isAmbiguousFacilityMatch)) return false;
  return true;
}

function isMissingDensityCause(v: unknown): v is MissingDensityCause {
  return MISSING_DENSITY_CAUSES.some((cause) => cause === v);
}

function isMissingDensityChemical(v: unknown): v is MissingDensityChemical {
  if (!isPlainObject(v)) return false;
  if (typeof v.facilityId !== "string") return false;
  if (!isNullableString(v.facilityName)) return false;
  if (typeof v.chemicalId !== "string") return false;
  if (!isNullableString(v.chemicalName)) return false;
  if (!isMissingDensityCause(v.cause)) return false;
  return true;
}

function isFacilityRef(v: unknown): v is FacilityRef {
  if (!isPlainObject(v)) return false;
  if (typeof v.facilityId !== "string") return false;
  if (!isNullableString(v.facilityName)) return false;
  return true;
}

/** `undefined` (historical record) or an array whose every entry passes `check`. */
function isOptionalArrayOf<T>(v: unknown, check: (x: unknown) => x is T): v is T[] | undefined {
  return v === undefined || (Array.isArray(v) && v.every(check));
}

/**
 * The list fields added after the first summaries were persisted. Each may be
 * absent on a historical `Job.output`; when present it must be well-formed.
 */
function hasValidOptionalLists(value: Record<string, unknown>): boolean {
  return (
    isOptionalArrayOf(value.openedReportFacilityIds, (x): x is string => typeof x === "string") &&
    isOptionalArrayOf(value.excludedProfileCodes, (x): x is string => typeof x === "string") &&
    isOptionalArrayOf(value.unmatchedSites, isUnmatchedSite) &&
    isOptionalArrayOf(value.missingDensityChemicals, isMissingDensityChemical) &&
    isOptionalArrayOf(value.facilitiesWithExistingOpenUpdate, isFacilityRef) &&
    isOptionalArrayOf(value.blockedFacilities, isBlockedFacility)
  );
}

function isInventoryChangeDetail(v: unknown): v is InventoryChangeDetail {
  if (!isPlainObject(v)) return false;
  if (typeof v.facilityId !== "string") return false;
  if (!isNullableString(v.facilityName)) return false;
  if (typeof v.chemicalId !== "string") return false;
  if (!isNullableString(v.chemicalName)) return false;
  if (!isInventoryChangeKind(v.kind)) return false;
  if (!isNullableNumber(v.priorMaxAmount)) return false;
  if (typeof v.newMaxAmount !== "number") return false;
  if (!isNullableNumber(v.priorMaxAmountCode)) return false;
  if (!isNullableNumber(v.newMaxAmountCode)) return false;
  if (!isNullableNumber(v.priorAverageAmount)) return false;
  if (typeof v.newAverageAmount !== "number") return false;
  if (!isNullableNumber(v.priorAverageAmountCode)) return false;
  if (!isNullableNumber(v.newAverageAmountCode)) return false;
  if (typeof v.unit !== "string") return false;
  if (typeof v.annualWasteAmount !== "number") return false;
  return true;
}

/**
 * Narrow a loosely-typed value (e.g. `Job.output` from the GraphQL `JSON`
 * scalar) into a `RunSummary` without an `as` cast. Returns `true` only when
 * every top-level field is present with the right kind AND every entry in
 * `cupaBreakdown` / `inventoryChanges` matches the expected shape. An upstream
 * code change that drops a field will fail this check up-front rather than
 * silently render empty cells in the customer-facing table.
 */
export function isWadmRunSummary(value: unknown): value is RunSummary {
  if (!isPlainObject(value)) return false;
  if (typeof value.wadmCycleId !== "string") return false;
  // Optional — historical Job.output records predate this field.
  if (value.contentHash !== undefined && typeof value.contentHash !== "string") return false;
  if (typeof value.facilitiesAttempted !== "number") return false;
  if (typeof value.facilitiesMatched !== "number") return false;
  if (typeof value.itemsUpdated !== "number") return false;
  if (typeof value.reportsCreated !== "number") return false;
  if (!isStringArray(value.facilityIdsWithExistingOpenUpdate)) return false;
  if (!isStringArray(value.unmatchedProfileCodes)) return false;
  if (!isStringArray(value.missingChemicals)) return false;
  if (!isStringArray(value.unmatchedSiteCodes)) return false;
  if (!hasValidOptionalLists(value)) return false;
  if (!Array.isArray(value.cupaBreakdown)) return false;
  if (!value.cupaBreakdown.every(isCupaBreakdownEntry)) return false;
  if (!Array.isArray(value.inventoryChanges)) return false;
  if (!value.inventoryChanges.every(isInventoryChangeDetail)) return false;
  return true;
}
